import React, { useEffect } from 'react';
import { Typography, Paper, Grid, Box, makeStyles, Container } from '@material-ui/core';
import PieChart from 'components/@common/Charts/PieChart';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { PrmClassroomEngagementCurriculumReportResult } from 'types/schema-generated-types';
import PrmTitleRow from '../common/PrmTitleRow';
import useCommonStyles from '../common/styles';
import SmallBarChart from '../common/SplitProgressBarChart/SmallBarChart';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { ReportEntityType } from 'components/ReportsModule/types';
import WithQuery from 'components/@common/CircularProgress/WithQuery';
import { buildChartValues, buildPieChartOptions } from './util';
import { capitalize } from 'lodash';
import MaybeShowErrorMessageWithLoading from '../../common/MaybeShowErrorMessageWithLoading';

type Props = {
  setList: any[];
  prmClassroomEngagementCurriculumReport: PrmClassroomEngagementCurriculumReportResult;
  setId?: (id?: any) => void;
  selectedId?: number;
  loading?: boolean;
  entityType: string;
};

const Engagement: React.FC<Props> = ({
  setList,
  prmClassroomEngagementCurriculumReport,
  setId,
  selectedId,
  loading,
  entityType
}) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const [, setQuery] = useReportQueryParams();

  const handleClick = (id: any) => {
    setQuery({ entityType: ReportEntityType.CLASSROOM, entityId: id });
  };

  // sets the first item as initial
  useEffect(() => {
    if (!setList?.[0]?.id) return;
    setId?.(setList?.[0]?.id);
  }, [setList, setId]);

  return (
    <>
      <Typography variant="h3" style={{ marginBottom: 6 }}>
        Engagement
      </Typography>
      <Paper className={commonClasses.container}>
        <Container>
          <PrmTitleRow
            title={`${capitalize(entityType)} Overview`}
            setList={setList}
            containerProps={{ marginBottom: 4 }}
            handleClick={setId}
            selected={selectedId}
          />
          <Grid container spacing={2} wrap="wrap">
            <MaybeShowErrorMessageWithLoading
              loading={loading!}
              width="100%"
              shouldRender={
                !!prmClassroomEngagementCurriculumReport?.classroomCurriculumProgress.length
              }
            >
              {prmClassroomEngagementCurriculumReport?.classroomCurriculumProgress?.map(
                (x, idx) => (
                  <Grid key={idx} item xs={6} style={{ maxWidth: 'unset' }}>
                    <Box width={520}>
                      <Box
                        style={{
                          display: 'flex',
                          flex: 1,
                          justifyContent: 'space-between',
                          marginBottom: 24
                        }}
                      >
                        <Typography className={classes.link}>{x.classroom?.name}</Typography>
                        <Typography
                          component="a"
                          onClick={() => handleClick(x?.classroom?.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#006DB8',
                            fontSize: 12,
                            fontWeight: 450,
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          <ArrowForward
                            fontSize="small"
                            style={{ color: '#006DB8', fontSize: 12 }}
                          />
                          <span>View Class Report</span>
                        </Typography>
                      </Box>
                      <Paper variant="outlined" className={classes.card}>
                        <Box className={classes.chartContainer}>
                          <WithQuery loading={loading} spinnerProps={{ xOffset: 20, yOffset: 30 }}>
                            <PieChart
                              type="donut"
                              series={buildChartValues(x)}
                              height={180}
                              options={buildPieChartOptions(x)}
                            />
                          </WithQuery>

                          <Typography className={classes.chartTitle}>
                            Total Lessons Complete
                          </Typography>
                        </Box>

                        {x.averageLessonCompletedCount > 0 && (
                          <SmallBarChart
                            completedPercent={x.overallCompletionStats.metSkillPercentage}
                            supportedPercent={
                              x.overallCompletionStats.metSkillWithSupportPercentage
                            }
                          />
                        )}
                      </Paper>
                    </Box>
                  </Grid>
                )
              )}
            </MaybeShowErrorMessageWithLoading>
          </Grid>
        </Container>
      </Paper>
    </>
  );
};

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flex: 1,
    height: 'auto',

    borderRadius: 12,
    padding: 10,
    flexWrap: 'wrap'
  },
  chartContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '40%',
    flexDirection: 'column'
  },
  chartTitle: {
    width: '50%',
    textAlign: 'center',
    fontWeight: 450,
    fontSize: 16
  },
  link: {
    fontWeight: 450,
    fontSize: 22
  }
}));
export default Engagement;
